'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import PanelSessionLoader from '@/components/PanelSessionLoader';
import { applyUserPanelTheme } from '@/lib/panelTheme';
import { normalizeStoredAvatarUrl, avatarFallbackInitial } from '@/lib/avatarPublic';
import { getUserSession, userLogout } from '@/lib/userSessionClient';
import { emitPanelLanguageSync } from '@/lib/panel-i18n/emitSync';
import {
  clearPanelSessionCache,
  hasPanelSessionCache,
  readPanelSessionCache,
  writePanelSessionCache,
} from '@/lib/panelSessionCache';

const UserAuthContext = createContext(null);

function isUserZone(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/user');
}

function isUserPublicPath(pathname) {
  if (pathname === '/user' || pathname === '/user/') return true;
  if (pathname === '/user/signup') return true;
  if (pathname === '/user/forgot-password') return true;
  return false;
}

function syncUserPanelLanguage(session) {
  // Only apply when the session API actually returned a language (not a client default).
  if (
    session?.ok &&
    session.preferredLanguage != null &&
    typeof session.preferredLanguage === 'string' &&
    session.preferredLanguage.trim()
  ) {
    emitPanelLanguageSync('user', session.preferredLanguage);
  }
}

function userProfileFromSession(username, email, name, avatarUrl) {
  const n = typeof name === 'string' ? name.trim() : '';
  const u = typeof username === 'string' ? username.trim() : '';
  const e = typeof email === 'string' ? email.trim() : '';
  const displayName = n || (u ? `@${u}` : 'User');
  const displayEmail = e || '—';
  const safeUrl = normalizeStoredAvatarUrl(typeof avatarUrl === 'string' ? avatarUrl : '') || '';
  const avatarInitial = avatarFallbackInitial(n, u, '?');
  return { displayName, displayEmail, avatarInitial, avatarUrl: safeUrl };
}

const IDLE_STATE = {
  status: 'idle',
  userId: null,
  username: null,
  email: null,
  name: null,
  preferredTheme: 'dark',
  avatarUrl: '',
};

function guestState() {
  return {
    status: 'unauthenticated',
    userId: null,
    username: null,
    email: null,
    name: null,
    preferredTheme: 'dark',
    avatarUrl: '',
  };
}

function authedFromSession(session) {
  return {
    status: 'authenticated',
    userId: session.userId,
    username: session.username,
    email: session.email,
    name: session.name ?? '',
    preferredTheme: session.preferredTheme === 'light' ? 'light' : 'dark',
    avatarUrl:
      normalizeStoredAvatarUrl(typeof session.avatarUrl === 'string' ? session.avatarUrl : '') || '',
  };
}

function sameAuthedSession(prev, next) {
  return (
    prev.status === 'authenticated' &&
    next.status === 'authenticated' &&
    prev.userId === next.userId &&
    prev.username === next.username &&
    prev.email === next.email &&
    prev.name === next.name &&
    prev.preferredTheme === next.preferredTheme &&
    prev.avatarUrl === next.avatarUrl
  );
}

function initialUserState() {
  // Client remount / Fast Refresh: restore so we never flash the session gate.
  return readPanelSessionCache('user') || IDLE_STATE;
}

export function UserAuthProvider({ children }) {
  const router = useRouter();
  const pathname = router.pathname;
  const [state, setState] = useState(initialUserState);
  const [avatarImageCacheKey, setAvatarImageCacheKey] = useState(0);
  const authenticatedRef = useRef(initialUserState().status === 'authenticated');
  const stateRef = useRef(state);
  const routerRef = useRef(router);

  stateRef.current = state;
  routerRef.current = router;

  const bumpAvatarImageCache = useCallback(() => {
    setAvatarImageCacheKey((k) => k + 1);
  }, []);

  const commitState = useCallback((next) => {
    setState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      authenticatedRef.current = resolved.status === 'authenticated';
      stateRef.current = resolved;
      if (resolved.status === 'authenticated') {
        writePanelSessionCache('user', resolved);
      } else if (resolved.status === 'unauthenticated') {
        clearPanelSessionCache('user');
      }
      return resolved;
    });
  }, []);

  useEffect(() => {
    if (!isUserZone(pathname) || state.status !== 'authenticated') return;
    applyUserPanelTheme(state.preferredTheme);
  }, [pathname, state.status, state.preferredTheme]);

  useEffect(() => {
    const cancelledRef = { current: false };

    (async () => {
      // Router not ready yet — do not wipe auth (causes sudden session gate).
      if (!router.isReady) return;

      if (!isUserZone(pathname)) {
        // Only clear when truly leaving /user — keep tab cache for return.
        if (stateRef.current.status !== 'idle') {
          stateRef.current = IDLE_STATE;
          setState(IDLE_STATE);
        }
        authenticatedRef.current = false;
        return;
      }

      const publicPath = isUserPublicPath(pathname);

      const cached = readPanelSessionCache('user');
      if (cached && stateRef.current.status !== 'authenticated') {
        authenticatedRef.current = true;
        stateRef.current = cached;
        setState(cached);
      }

      // Already in the panel — never re-gate or background-check mid-work.
      if (
        !publicPath &&
        (authenticatedRef.current || stateRef.current.status === 'authenticated')
      ) {
        return;
      }

      // Cold start / hard URL only.
      if (stateRef.current.status !== 'authenticated') {
        commitState((s) => (s.status === 'authenticated' ? s : { ...s, status: 'loading' }));
      }

      const session = await getUserSession();
      if (cancelledRef.current) return;

      if (publicPath) {
        if (session.ok) {
          commitState(authedFromSession(session));
          syncUserPanelLanguage(session);
          void routerRef.current.replace('/user/dashboard');
        } else if (!session.networkError) {
          commitState(guestState());
        }
        return;
      }

      if (!session.ok) {
        if (session.networkError) return;
        commitState(guestState());
        void routerRef.current.replace('/user');
        return;
      }

      commitState(authedFromSession(session));
      syncUserPanelLanguage(session);
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [pathname, commitState, router.isReady]);

  const refresh = useCallback(async () => {
    if (!isUserZone(pathname)) return;
    const publicPath = isUserPublicPath(pathname);
    // Never swap the working panel for a full-screen session gate on refresh.
    const session = await getUserSession();
    if (session.networkError) return;
    if (publicPath) {
      if (session.ok) {
        commitState(authedFromSession(session));
        syncUserPanelLanguage(session);
        void routerRef.current.replace('/user/dashboard');
      } else {
        commitState(guestState());
      }
      return;
    }
    if (!session.ok) {
      commitState(guestState());
      void routerRef.current.replace('/user');
      return;
    }
    const next = authedFromSession(session);
    if (!sameAuthedSession(stateRef.current, next)) {
      commitState(next);
    }
    syncUserPanelLanguage(session);
  }, [pathname, commitState]);

  const applyUserProfileToSession = useCallback(
    (profile) => {
      if (!profile || typeof profile !== 'object') return;
      commitState((s) => {
        if (s.status !== 'authenticated') return s;
        const next = { ...s };
        if (typeof profile.username === 'string') next.username = profile.username;
        if (typeof profile.email === 'string') next.email = profile.email;
        if (typeof profile.name === 'string') next.name = profile.name;
        if (profile.preferredTheme === 'light' || profile.preferredTheme === 'dark') {
          next.preferredTheme = profile.preferredTheme;
          applyUserPanelTheme(profile.preferredTheme);
        }
        if ('avatarUrl' in profile) {
          next.avatarUrl =
            typeof profile.avatarUrl === 'string'
              ? normalizeStoredAvatarUrl(profile.avatarUrl) || ''
              : '';
        }
        if (typeof profile.preferredLanguage === 'string' && profile.preferredLanguage.trim()) {
          emitPanelLanguageSync('user', profile.preferredLanguage.trim());
        }
        return next;
      });
    },
    [commitState]
  );

  const persistPreferredTheme = useCallback(
    async (next) => {
      const t = next === 'light' ? 'light' : 'dark';
      applyUserPanelTheme(t);
      try {
        const res = await fetch('/api/user/profile', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferredTheme: t }),
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (data.profile && typeof data.profile === 'object') {
          applyUserProfileToSession(data.profile);
        }
        const session = await getUserSession();
        if (session.ok) {
          commitState(authedFromSession(session));
          syncUserPanelLanguage(session);
        }
      } catch {
        /* DOM already updated */
      }
    },
    [applyUserProfileToSession, commitState]
  );

  const signOut = useCallback(async () => {
    await userLogout();
    commitState(guestState());
    applyUserPanelTheme('dark');
    await routerRef.current.push('/user');
  }, [commitState]);

  const value = useMemo(() => {
    const { displayName, displayEmail, avatarInitial, avatarUrl } = userProfileFromSession(
      state.username,
      state.email,
      state.name,
      state.avatarUrl
    );
    return {
      status: state.status,
      userId: state.userId,
      username: state.username,
      email: state.email,
      name: state.name,
      preferredTheme: state.preferredTheme === 'light' ? 'light' : 'dark',
      avatarUrl,
      avatarImageCacheKey,
      bumpAvatarImageCache,
      displayName,
      displayEmail,
      avatarInitial,
      isAuthenticated: state.status === 'authenticated',
      refresh,
      applyUserProfileToSession,
      persistPreferredTheme,
      signOut,
    };
  }, [
    state.status,
    state.userId,
    state.username,
    state.email,
    state.name,
    state.preferredTheme,
    state.avatarUrl,
    avatarImageCacheKey,
    bumpAvatarImageCache,
    refresh,
    applyUserProfileToSession,
    persistPreferredTheme,
    signOut,
  ]);

  const inUser = isUserZone(pathname);
  const publicRoute = isUserPublicPath(pathname);
  const protectedUser = inUser && !publicRoute;

  // Never tear down an unlocked panel (cache / in-memory auth) for a session flash.
  const keepPanel =
    state.status === 'authenticated' ||
    authenticatedRef.current ||
    hasPanelSessionCache('user');

  if (protectedUser && state.status !== 'authenticated' && !keepPanel) {
    return (
      <UserAuthContext.Provider value={value}>
        <PanelSessionLoader variant="user" />
      </UserAuthContext.Provider>
    );
  }

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx) {
    throw new Error('useUserAuth must be used within UserAuthProvider');
  }
  return ctx;
}
